import { 
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithCredential
} from 'firebase/auth';
import { auth, googleProvider, db } from './firebase';
import { doc, setDoc, getDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Capacitor } from '@capacitor/core';

let isGoogleAuthInitialized = false;

export const authService = {
  async initGoogleAuth() {
    if (Capacitor.isNativePlatform() && !isGoogleAuthInitialized) {
      try {
        await GoogleAuth.initialize({
          clientId: '140091084496-jdiai6hd512o0c6aijvf7qq7pu8u9snd.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
          grantOfflineAccess: true
        });
        isGoogleAuthInitialized = true;
      } catch (e) {
        console.warn('Error al inicializar GoogleAuth en Capacitor:', e);
      }
    }
  },

  async loginWithGoogle() {
    try {
      await this.initGoogleAuth();
      let userCredentialUser = null;

      if (Capacitor.isNativePlatform()) {
        const googleUser = await GoogleAuth.signIn();
        const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
        const result = await signInWithCredential(auth, credential);
        userCredentialUser = result.user;
      } else {
        const result = await signInWithPopup(auth, googleProvider);
        userCredentialUser = result.user;
      }

      if (userCredentialUser) {
        const email = (userCredentialUser.email || '').toLowerCase().trim();
        const userDocRef = doc(db, 'usuarios', userCredentialUser.uid);
        const userDoc = await getDoc(userDocRef);

        let existingUserDoc = null;
        let existingUserId = null;

        if (userDoc.exists()) {
          existingUserDoc = userDoc.data();
          existingUserId = userDoc.id;
        } else {
          // Buscar si el correo fue pre-registrado por el administrador
          const q = query(collection(db, 'usuarios'), where('email', '==', email));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const firstDoc = querySnapshot.docs[0];
            existingUserDoc = firstDoc.data();
            existingUserId = firstDoc.id;
          }
        }

        // Caso especial: cuenta demo
        const isDemo = email === 'demo@comarsa.com';

        // ❌ RECHAZAR si no está pre-autorizado por el Admin
        if (!existingUserDoc && !isDemo) {
          await signOut(auth);
          return {
            success: false,
            error: `Acceso no autorizado: El correo "${email}" no está registrado en la plantilla de colaboradores. Contacta a Recursos Humanos.`
          };
        }

        // ❌ RECHAZAR si el usuario está dado de baja o inactivo
        if (existingUserDoc && existingUserDoc.activo === false && !isDemo) {
          await signOut(auth);
          return {
            success: false,
            error: 'Tu cuenta se encuentra inactiva. Contacta al Administrador de Grupo Comarsa.'
          };
        }

        // Si el usuario fue pre-registrado bajo otro UID (creado por admin), migrarlo a su Google UID
        if (existingUserId && existingUserId !== userCredentialUser.uid) {
          await setDoc(userDocRef, {
            ...existingUserDoc,
            email,
            nombre: existingUserDoc.nombre || userCredentialUser.displayName || email.split('@')[0],
            googleLinked: true,
            ultimoAcceso: new Date()
          });
          try {
            await deleteDoc(doc(db, 'usuarios', existingUserId));
          } catch (delErr) {
            console.warn('No se pudo borrar doc previo:', delErr);
          }
        } else if (!userDoc.exists() && isDemo) {
          await setDoc(userDocRef, {
            email: 'demo@comarsa.com',
            nombre: 'Usuario Demo',
            rol: 'empleado',
            activo: true,
            faceRegistered: false,
            fechaRegistro: new Date()
          });
        }
      }

      return { success: true, user: userCredentialUser };
    } catch (error) {
      console.error('Error en login con Google:', error);
      return { success: false, error: error.message };
    }
  },

  async loginWithEmail(email, password) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;
      if (user) {
        const userDocRef = doc(db, 'usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.activo === false && user.email?.toLowerCase() !== 'demo@comarsa.com') {
            await signOut(auth);
            return {
              success: false,
              error: 'Tu cuenta se encuentra inactiva. Contacta al Administrador.'
            };
          }
        }
      }
      return { success: true, user: result.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async registerWithEmail(email, password, nombre) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;
      await setDoc(doc(db, 'usuarios', user.uid), {
        email,
        nombre,
        rol: 'empleado',
        activo: true,
        fechaRegistro: new Date(),
        faceRegistered: false
      });
      return { success: true, user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async logout() {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getCurrentUserData(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'usuarios', userId));
      if (userDoc.exists()) {
        return { success: true, data: userDoc.data() };
      }
      return { success: false, error: 'Usuario no encontrado' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};